import React, { useCallback, useEffect, useState } from "react";
import { http } from "../lib/api";
import { Plus, X, Copy } from "@phosphor-icons/react";

export default function AIInterviews() {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [flash, setFlash] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await http.get("/ai-interviews");
      setInterviews(r.data || []);
    } catch (e) {
      setFlash({ kind: "error", msg: `Failed to load: ${e.message}` });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const statusPill = (s) => {
    const cls = s === "completed" ? "bg-emerald-100 text-emerald-800"
      : s === "in_progress" ? "bg-blue-100 text-blue-800"
      : "bg-slate-100 text-slate-700";
    return <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${cls}`}>{s || "created"}</span>;
  };

  return (
    <div className="space-y-5" data-testid="ai-interviews-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Vaku · AI Interviews</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">AI Video Interviews</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Send candidates a self-serve video screening. Vaku transcribes their answers and scores them against the job.
          </p>
        </div>
        <button
          data-testid="new-ai-interview-btn"
          className="btn btn-primary"
          onClick={() => setCreateOpen(true)}
        >
          <Plus size={16} /> New AI Interview
        </button>
      </div>

      {flash && (
        <div className={`card p-3 text-sm flex items-start gap-2 ${flash.kind === "error" ? "!border-red-300 !bg-red-50 text-red-800" : "!border-emerald-300 !bg-emerald-50 text-emerald-800"}`}>
          <span className="flex-1">{flash.msg}</span>
          <button className="btn-ghost p-0" onClick={() => setFlash(null)}><X size={14} /></button>
        </div>
      )}

      <div className="card overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th>Candidate</th>
              <th>Role</th>
              <th>Questions</th>
              <th>Status</th>
              <th style={{ width: 100 }}>Score</th>
              <th>Created</th>
              <th style={{ width: 160 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center text-slate-400 py-6">Loading…</td></tr>}
            {!loading && interviews.length === 0 && (
              <tr><td colSpan={7} className="text-center text-slate-400 py-6" data-testid="ai-interviews-empty">
                No AI interviews yet. Click <em>New AI Interview</em> to send one.
              </td></tr>
            )}
            {interviews.map((it) => (
              <tr key={it.id} data-testid={`ai-interview-row-${it.id}`}>
                <td className="font-medium">
                  {it.candidateName}
                  <div className="text-[11px] text-slate-500 font-normal">{it.candidateEmail}</div>
                </td>
                <td>{it.jobTitle}{it.requisitionNumber ? ` · Req #${it.requisitionNumber}` : ""}</td>
                <td className="font-mono text-xs">{it.answers?.length ?? 0} / {it.questions?.length ?? 0}</td>
                <td>{statusPill(it.status)}</td>
                <td className="font-mono font-bold">{it.evaluation?.score ?? "—"}</td>
                <td className="font-mono text-xs text-slate-500">{new Date(it.createdAt).toLocaleDateString()}</td>
                <td>
                  <button
                    className="text-xs text-[#002FA7] hover:underline"
                    onClick={() => setSelected(it)}
                    data-testid={`ai-interview-view-${it.id}`}
                  >
                    View →
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {createOpen && (
        <CreateInterviewModal
          onClose={() => setCreateOpen(false)}
          onCreated={async (interview) => {
            setCreateOpen(false);
            const em = interview.emailResult || {};
            const emailNote = em?.sent
              ? `Email sent to ${(em.to || []).join(", ")}.`
              : em?.error ? `(Email failed: ${em.error})` : "";
            setFlash({ kind: "ok", msg: `AI interview created. ${emailNote} Share: ${interview.interviewUrl}` });
            await load();
          }}
        />
      )}
      {selected && (
        <InterviewDetail
          interview={selected}
          onClose={() => setSelected(null)}
          onRefresh={load}
        />
      )}
    </div>
  );
}

function CreateInterviewModal({ onClose, onCreated }) {
  const [candidates, setCandidates] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [candidateId, setCandidateId] = useState("");
  const [reqNumber, setReqNumber] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [coreText, setCoreText] = useState("");
  const [timeLimit, setTimeLimit] = useState(30);
  const [questionCap, setQuestionCap] = useState(8);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    http.get("/candidates").then((r) => setCandidates(r.data || [])).catch(() => {});
    http.get("/fusion/requisitions").then((r) => setReqs(r.data?.items || [])).catch(() => {});
  }, []);

  const pickReq = (num) => {
    setReqNumber(num);
    const r = reqs.find((x) => x.RequisitionNumber === num);
    if (r) {
      setJobTitle(r.Title || "");
      setJobDescription(r.Description || "");
    }
  };

  const submit = async () => {
    setErr(null);
    if (!candidateId || !jobTitle.trim()) {
      setErr("Pick a candidate and enter a job title.");
      return;
    }
    setBusy(true);
    try {
      const core = coreText.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, questionCap);
      const r = await http.post("/ai-interviews", {
        candidateId: parseInt(candidateId, 10),
        requisitionNumber: reqNumber || null,
        jobTitle: jobTitle.trim(),
        jobDescription,
        coreQuestions: core,
        timeLimitMinutes: parseInt(timeLimit, 10) || 30,
        questionCap: parseInt(questionCap, 10) || 8,
        sendEmail: true,
      });
      onCreated(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/50" data-testid="ai-interview-modal">
      <div className="card w-full max-w-2xl p-6 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display text-xl font-bold">New AI Video Interview</h2>
            <div className="text-xs text-slate-500 mt-0.5">Vaku generates {questionCap - (coreText.split("\n").filter((l) => l.trim()).length)} extra questions from the JD + candidate&apos;s CV.</div>
          </div>
          <button onClick={onClose} className="btn-ghost p-1"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-eyebrow block mb-1">Candidate *</label>
            <select
              data-testid="ai-int-candidate"
              className="input"
              value={candidateId}
              onChange={(e) => setCandidateId(e.target.value)}
            >
              <option value="">— Pick a candidate —</option>
              {candidates.map((c) => (
                <option key={c.id} value={c.id}>#{c.id} · {c.name} · {c.email}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Requisition (optional)</label>
            <select
              data-testid="ai-int-req"
              className="input"
              value={reqNumber}
              onChange={(e) => pickReq(e.target.value)}
            >
              <option value="">— None —</option>
              {reqs.map((r) => (
                <option key={r.RequisitionNumber} value={r.RequisitionNumber}>
                  {r.RequisitionNumber} — {r.Title}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1">Job Title *</label>
            <input data-testid="ai-int-title" className="input" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1">Job Description (used to generate questions)</label>
            <textarea data-testid="ai-int-jd" className="input" rows={3} value={jobDescription} onChange={(e) => setJobDescription(e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1">Your core questions (one per line — Vaku fills the rest)</label>
            <textarea
              data-testid="ai-int-core"
              className="input"
              rows={4}
              value={coreText}
              onChange={(e) => setCoreText(e.target.value)}
              placeholder={"Walk me through a project where you led a team of 5+.\nWhat's your most complex HCM implementation?"}
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Time limit (minutes)</label>
            <input data-testid="ai-int-time" type="number" min={5} max={120} className="input" value={timeLimit} onChange={(e) => setTimeLimit(e.target.value)} />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Question cap</label>
            <input data-testid="ai-int-cap" type="number" min={3} max={15} className="input" value={questionCap} onChange={(e) => setQuestionCap(e.target.value)} />
          </div>
        </div>
        {err && <div className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">{err}</div>}
        <div className="mt-5 flex justify-end gap-2">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button
            data-testid="ai-int-submit"
            className="btn btn-primary"
            disabled={busy}
            onClick={submit}
          >
            {busy ? "Creating…" : "Create & Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

function InterviewDetail({ interview: initial, onClose, onRefresh }) {
  const [interview, setInterview] = useState(initial);
  const [videoUrls, setVideoUrls] = useState({});

  const refresh = async () => {
    try {
      const r = await http.get(`/ai-interviews/${initial.id}`);
      setInterview(r.data);
      onRefresh?.();
    } catch { /* ignore */ }
  };

  useEffect(() => {
    let cancelled = false;
    const urls = {};
    const answers = interview.answers || [];
    (async () => {
      for (const a of answers) {
        try {
          const r = await http.get(`/ai-interviews/${interview.id}/video/${a.questionIndex}`, {
            responseType: "blob",
          });
          if (cancelled) return;
          urls[a.questionIndex] = URL.createObjectURL(r.data);
          setVideoUrls((prev) => ({ ...prev, [a.questionIndex]: urls[a.questionIndex] }));
        } catch { /* skip */ }
      }
    })();
    return () => {
      cancelled = true;
      Object.values(urls).forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interview.id, interview.answers?.length]);

  const shareUrl = `${window.location.origin}/interview/${interview.token}`;
  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
  };
  const evalData = interview.evaluation || {};
  const answers = interview.answers || [];

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 overflow-y-auto" data-testid="ai-interview-detail">
      <div className="max-w-4xl mx-auto my-8 card p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="font-display text-xl font-bold">{interview.candidateName} — {interview.jobTitle}</h2>
            <div className="text-xs text-slate-500">Status: <strong>{interview.status}</strong> · {answers.length}/{interview.questions?.length ?? 0} answered</div>
          </div>
          <div className="flex gap-2">
            <button className="btn btn-outline !text-xs" onClick={refresh}>Refresh</button>
            <button onClick={onClose} className="btn-ghost p-1"><X size={18} /></button>
          </div>
        </div>

        <div className="card p-3 !bg-slate-50 mb-4 text-xs flex items-center gap-2" data-testid="ai-interview-link">
          <span className="text-slate-500">Candidate link:</span>
          <code className="font-mono flex-1 truncate">{shareUrl}</code>
          <button className="btn btn-outline !text-xs !py-1" onClick={copyLink} data-testid="ai-interview-copy-link">
            <Copy size={12} /> Copy
          </button>
        </div>

        {interview.teamsMeeting?.joinUrl && (
          <div className="card p-3 !bg-indigo-50 !border-indigo-200 mb-4" data-testid="ai-interview-teams">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-indigo-900">Microsoft Teams meeting created ✓</span>
              <a
                href={interview.teamsMeeting.joinUrl}
                target="_blank" rel="noopener noreferrer"
                className="text-xs text-indigo-700 hover:underline font-mono truncate flex-1 min-w-0"
                data-testid="ai-interview-teams-link"
              >{interview.teamsMeeting.joinUrl}</a>
              <button
                className="btn btn-outline !text-xs !py-1"
                onClick={() => navigator.clipboard.writeText(interview.teamsMeeting.joinUrl)}
                data-testid="ai-interview-teams-copy"
              ><Copy size={12} /> Copy</button>
              <button
                className="btn btn-primary !text-xs !py-1"
                onClick={async () => {
                  try {
                    const r = await http.post(`/ai-interviews/${interview.id}/fetch-teams-transcript`);
                    if (r.data?.status === "ready") {
                      await refresh();
                    } else {
                      window.alert(`Transcript ${r.data?.status}: ${r.data?.reason || ""}`);
                    }
                  } catch (e) {
                    window.alert(e.response?.data?.detail || e.message);
                  }
                }}
                data-testid="ai-interview-teams-fetch"
              >Fetch Teams transcript</button>
            </div>
          </div>
        )}

        {interview.status === "completed" && evalData?.score !== undefined && (
          <div className="card p-4 mb-4 !bg-emerald-50 !border-emerald-200" data-testid="ai-interview-evaluation">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="label-eyebrow">Vaku AI Evaluation</div>
                <h3 className="font-display font-bold text-lg">Overall Score: <span className="text-emerald-700">{evalData.score}</span> — {evalData.recommendation}</h3>
              </div>
            </div>
            {evalData.summary && <p className="text-sm text-slate-700 mb-3">{evalData.summary}</p>}
            {evalData.competencies && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                {Object.entries(evalData.competencies).map(([k, v]) => (
                  <div key={k} className="border rounded p-2 bg-white">
                    <div className="text-[10px] text-slate-500 uppercase">{k}</div>
                    <div className="font-mono font-bold">{v}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div>
                <div className="label-eyebrow text-emerald-700 mb-1">Strengths</div>
                <ul className="list-disc ml-4 space-y-0.5">{(evalData.strengths || []).map((s, i) => <li key={i}>{s}</li>)}</ul>
              </div>
              <div>
                <div className="label-eyebrow text-red-700 mb-1">Concerns</div>
                <ul className="list-disc ml-4 space-y-0.5">{(evalData.concerns || []).map((s, i) => <li key={i}>{s}</li>)}</ul>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {(interview.questions || []).map((q, idx) => {
            const ans = answers.find((a) => a.questionIndex === idx);
            return (
              <div key={idx} className="border rounded-md p-3" data-testid={`ai-interview-q-${idx}`}>
                <div className="font-medium text-sm">Q{idx + 1}. {q}</div>
                {ans ? (
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {videoUrls[idx] ? (
                      <video controls className="w-full rounded" src={videoUrls[idx]} />
                    ) : (
                      <div className="w-full rounded bg-slate-100 grid place-items-center text-slate-400 text-xs h-40">Loading video…</div>
                    )}
                    <div className="text-xs text-slate-700">
                      <div className="label-eyebrow !text-[10px] mb-1">Transcript</div>
                      {ans.transcript
                        ? <div className="whitespace-pre-wrap">{ans.transcript}</div>
                        : <div className="text-slate-400 italic">Transcription pending or unavailable.</div>}
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-slate-400 italic mt-1">Not answered yet.</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
